import React, { useState, useMemo } from "react";
import {
  Code,
  Database,
  Zap,
  Cloud,
  Mail,
  BarChart3,
  Share2,
  FileCode,
  MessageSquare,
  Bot,
  Server,
  GitBranch,
  Layers,
  Cpu,
  Globe,
  GitCommit,
  Search,
} from "lucide-react";
import AIWorkflowShowcase from "@/components/AIWorkflowShowcase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// -------------------------------------------------------------
// Listas de integrações
// -------------------------------------------------------------
const mainIntegrations = [
  { name: "APIs REST", icon: Code, color: "primary" },
  { name: "Bancos de Dados", icon: Database, color: "secondary" },
  { name: "Webhooks", icon: Zap, color: "accent" },
  { name: "ChatGPT", icon: MessageSquare, color: "primary" },
  { name: "PostgreSQL", icon: Database, color: "secondary" },
  { name: "Google Cloud", icon: Cloud, color: "accent" },
];

const additionalIntegrations = [
  { name: "Azure DevOps", icon: Cloud, color: "primary" },
  { name: "HTML", icon: FileCode, color: "secondary" },
  { name: "LinkedIn", icon: Share2, color: "accent" },
  { name: "Gemini", icon: Bot, color: "primary" },
  { name: "Smart Agent Hub", icon: Zap, color: "secondary" },
  { name: "Email", icon: Mail, color: "accent" },
  { name: "Dashboard Generator", icon: BarChart3, color: "primary" },
  { name: "Microsoft SharePoint", icon: Share2, color: "secondary" },
];

const moreIntegrations = [
  { name: "AWS Lambda", icon: Cloud, color: "accent" },
  { name: "Supabase", icon: Database, color: "secondary" },
  { name: "MongoDB", icon: Server, color: "primary" },
  { name: "Firebase", icon: Zap, color: "accent" },
  { name: "Notion", icon: Layers, color: "secondary" },
  { name: "GitHub", icon: GitBranch, color: "primary" },
  { name: "Jira", icon: GitCommit, color: "accent" },
  { name: "Slack", icon: MessageSquare, color: "secondary" },
  { name: "OpenAI API", icon: Cpu, color: "primary" },
  { name: "Twilio", icon: Globe, color: "accent" },
  { name: "Microsoft Teams", icon: MessageSquare, color: "primary" },
  { name: "Zapier", icon: Zap, color: "secondary" },
  { name: "Make (Integromat)", icon: Layers, color: "accent" },
  { name: "Power BI", icon: BarChart3, color: "primary" },
  { name: "Salesforce", icon: Cloud, color: "secondary" },
  { name: "HubSpot", icon: Share2, color: "accent" },
  { name: "WhatsApp Cloud API", icon: MessageSquare, color: "primary" },
  { name: "Telegram Bots", icon: Bot, color: "secondary" },
  { name: "Discord", icon: MessageSquare, color: "accent" },
  { name: "Asana", icon: GitCommit, color: "primary" },
  { name: "Trello", icon: GitBranch, color: "secondary" },
  { name: "Airtable", icon: Database, color: "accent" },
  { name: "MySQL", icon: Database, color: "primary" },
  { name: "Oracle Cloud", icon: Cloud, color: "secondary" },
  { name: "Klaviyo", icon: Mail, color: "accent" },
  { name: "SendGrid", icon: Mail, color: "primary" },
  { name: "Mailchimp", icon: Mail, color: "secondary" },
  { name: "Stripe", icon: Zap, color: "accent" },
  { name: "PayPal", icon: Zap, color: "primary" },
  { name: "Google Sheets", icon: FileCode, color: "secondary" },
];

// -------------------------------------------------------------
// Componente principal
// -------------------------------------------------------------
const Integrations = () => {
  const [search, setSearch] = useState("");

  const filteredIntegrations = useMemo(
    () =>
      moreIntegrations.filter((integration) =>
        integration.name.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  return (
    <>
      {/* 🌟 Showcase acima das integrações */}
      <section className="py-24 bg-gradient-to-b from-background via-background/80 to-transparent">
        <div className="container mx-auto px-4 lg:px-8">
          <AIWorkflowShowcase
            title="AI Workflow Builder"
            videoSrc="/assets/workflow-demo.mp4"
            onShowResult={() => console.log("Resultado clicado!")}
            onShowFlow={() => console.log("Flow clicado!")}
          />
        </div>
      </section>

      {/* 🔗 Seção principal de Integrações */}
      <section id="integracoes" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-gradient">Integrações sem Fronteiras</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Conecte-se com as principais plataformas do mercado. Mais de 100
              integrações prontas para usar.
            </p>
          </div>

          {/* Cards principais com Animação de Scroll Infinito */}
          <div className="relative overflow-hidden mb-12 py-4">
            <div className="flex gap-6 w-max animate-scroll-right pause-on-hover">
              {/* Triplicando a lista para garantir loop suave em telas grandes */}
              {[...mainIntegrations, ...mainIntegrations, ...mainIntegrations].map((integration, index) => (
                <div key={index} className="w-[280px] flex-shrink-0">
                  <IntegrationCard
                    name={integration.name}
                    icon={integration.icon}
                    color={integration.color}
                  />
                </div>
              ))}
            </div>
            {/* Gradientes para suavizar as bordas */}
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          </div>

          {/* Botão Ver Mais com Modal e Busca */}
          <div className="text-center mb-12">
            <Dialog>
              <DialogTrigger asChild>
                <button className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-6 py-3 hover:bg-primary/20 transition-colors group">
                  <span className="text-accent font-semibold">
                    Ver Mais Integrações
                  </span>
                  <Zap className="w-4 h-4 text-accent group-hover:scale-110 transition-transform" />
                </button>
              </DialogTrigger>

              <DialogContent className="max-w-5xl bg-background/95 backdrop-blur-xl border border-border rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="text-center text-2xl font-bold text-accent mb-6">
                    Mais Integrações Disponíveis
                  </DialogTitle>
                </DialogHeader>

                {/* 🔍 Busca */}
                <div className="relative mb-4 flex justify-center">
                  <Search className="absolute left-1/2 -translate-x-[calc(50%-6.5rem)] top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar integração..."
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full md:w-1/2 pl-8 pr-4 py-2 rounded-full border border-border bg-background/70 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent transition-all"
                  />
                </div>

                {/* Grade rolável */}
                <div className="max-h-[70vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-accent/40 scrollbar-track-transparent">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 pb-4">
                    {filteredIntegrations.length > 0 ? (
                      filteredIntegrations.map((integration, index) => (
                        <IntegrationCard
                          key={index}
                          name={integration.name}
                          icon={integration.icon}
                          color={integration.color}
                          compact
                        />
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center col-span-full py-8">
                        Nenhuma integração encontrada 😢
                      </p>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Integrações adicionais visíveis com Scroll Mais Lento */}
          <div className="relative overflow-hidden mb-12 py-4">
            <div className="flex gap-4 w-max animate-scroll-right-slow pause-on-hover">
              {/* Triplicando a lista para garantir loop suave */}
              {[...additionalIntegrations, ...additionalIntegrations, ...additionalIntegrations].map((integration, index) => (
                <div key={index} className="w-[200px] flex-shrink-0">
                  <IntegrationCard
                    name={integration.name}
                    icon={integration.icon}
                    color={integration.color}
                    compact
                  />
                </div>
              ))}
            </div>
            {/* Gradientes para suavizar as bordas */}
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          </div>

          {/* Rodapé de integrações */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 rounded-full px-6 py-3">
              <Zap className="w-5 h-5 text-accent" />
              <span className="text-accent font-medium">
                + de 100 integrações disponíveis
              </span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// -------------------------------------------------------------
// Card de integração
// -------------------------------------------------------------
const IntegrationCard = ({
  name,
  icon: Icon,
  color,
  compact = false,
}: {
  name: string;
  icon: any;
  color: string;
  compact?: boolean;
}) => {
  const colorMap = {
    primary:
      "border-primary/30 hover:border-primary bg-primary/5 hover:bg-primary/10 text-primary",
    secondary:
      "border-secondary/30 hover:border-secondary bg-secondary/5 hover:bg-secondary/10 text-secondary",
    accent:
      "border-accent/30 hover:border-accent bg-accent/5 hover:bg-accent/10 text-accent",
  };

  return (
    <div
      className={`group relative ${compact ? "p-4" : "p-8"
        } rounded-2xl border-2 ${colorMap[color as keyof typeof colorMap]
        } backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer`}
    >
      <div className="flex flex-col items-center space-y-3">
        <div className={`${compact ? "p-2" : "p-4"} rounded-xl bg-background/50`}>
          <Icon className={compact ? "w-5 h-5" : "w-8 h-8"} />
        </div>
        <h3
          className={`font-semibold ${compact ? "text-xs" : "text-lg"
            } text-foreground text-center`}
        >
          {name}
        </h3>
      </div>

      {/* Glow effect */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity blur-xl -z-10 bg-current" />
    </div>
  );
};

export default Integrations;
